import type { IPlayerUpdateEventPayload } from '@lucimark/shared/models/player-update'
import type { TWebOtaApplyTiming } from '@lucimark/shared/models/tenant'
import type { IAuthenticationService } from './AuthenticationService'
import { shouldSkipAndroidApkOtaForNativeShell } from '@lucimark/shared/helpers/playerAppVersion'
import Emittery from 'emittery'
import { VERSION as EMBEDDED_PLAYER_PACKAGE_VERSION } from '@/constants'
import { addPlayerApkOtaSentryBreadcrumb } from '@/helpers/addPlayerApkOtaSentryBreadcrumb'
import { isAndroidPlayStoreDistributionResolved } from '@/helpers/androidDistribution'
import { isDeferredApkCacheInvalidError } from '@/helpers/apkOtaDeferredErrors'
import { getNativeShellAppVersionName, getResolvedPlayerAppVersion } from '@/helpers/resolvedPlayerAppVersion'
import { getPlatform, getPlatformInstance } from '@/platform'

const PLAYER_DEVICE_APK_OTA_TAG = 'player_apk_ota'

/** JSON to stdout / Android logcat; filter `adb logcat | grep player_apk_ota`. */
function logPlayerDeviceApkOta(payload: Record<string, unknown>, otaTraceId?: string): void {
  const row = {
    tag: PLAYER_DEVICE_APK_OTA_TAG,
    ...(otaTraceId != null && otaTraceId !== '' ? { otaTraceId } : {}),
    ...payload
  }
  console.info(JSON.stringify(row))
  addPlayerApkOtaSentryBreadcrumb(row)
}

/**
 * Skip re-downloading the same offered version shortly after we finished streaming the APK and opened the
 * installer. Must survive restarts longer than a quick reconnect (15m was too short for “install later”).
 * The install-intent record is written **before** `updater_finish_download` so it survives the WebView pausing
 * when the system installer opens (writing only after finish often never reached disk).
 */
const ANDROID_INSTALL_INTENT_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000
const ANDROID_INSTALL_INTENT_STORE_KEY = 'player_android_apk_install_intent_v1'
/** APK fully streamed to `update.apk` but `updater_finish_download` deferred until next process start (`next_app_restart`). */
const ANDROID_DEFERRED_APK_INSTALL_STORE_KEY = 'player_android_apk_deferred_install_v1'

function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binary = ''
  for (let i = 0; i < bytes.length; i++) { binary += String.fromCharCode(bytes[i]!) }
  return btoa(binary)
}

/** Minimal event payloads for update flow (no Capgo types). */
export interface UpdateAvailableEvent { version: string }
export interface DownloadEvent { version: string, progress: number }
export interface DownloadCompleteEvent {
  version: string
  /** APK saved to cache; system installer intentionally deferred until next app restart. */
  installPendingRestart?: boolean
}
export interface DownloadFailedEvent { version: string, error: string }
export interface UpdateFailedEvent { version: string, error: string }
export interface NoNeedEvent { version: string }

export enum PlayerUpdateServiceEvent {
  NO_NEED_UPDATE = 'no_need_update',
  UPDATE_AVAILABLE = 'update_available',
  DOWNLOAD = 'download',
  DOWNLOAD_COMPLETE = 'download_complete',
  DOWNLOAD_FAILED = 'download_failed',
  UPDATE_FAILED = 'update_failed'
}

export interface PlayerUpdateServiceEventMap {
  [PlayerUpdateServiceEvent.NO_NEED_UPDATE]: NoNeedEvent
  [PlayerUpdateServiceEvent.UPDATE_AVAILABLE]: UpdateAvailableEvent
  [PlayerUpdateServiceEvent.DOWNLOAD]: DownloadEvent
  [PlayerUpdateServiceEvent.DOWNLOAD_COMPLETE]: DownloadCompleteEvent
  [PlayerUpdateServiceEvent.DOWNLOAD_FAILED]: DownloadFailedEvent
  [PlayerUpdateServiceEvent.UPDATE_FAILED]: UpdateFailedEvent
}

export interface IPlayerUpdaterUpdateOptions {
  /** Registered tenant policy; omitted = immediate (install after download). `next_app_restart` streams to disk then installs on next app start (or when policy flips to immediate). */
  webOtaApplyTiming?: TWebOtaApplyTiming
}

export interface IPlayerUpdaterService {
  update: (update: IPlayerUpdateEventPayload, opts?: IPlayerUpdaterUpdateOptions) => Promise<void>
  reset: () => Promise<void>
}

export class PlayerUpdaterService extends Emittery<PlayerUpdateServiceEventMap> implements IPlayerUpdaterService {
  private authenticationService: IAuthenticationService
  private mode: string
  private baseURL: string
  /**
   * Android: after `updater_finish_download` the system installer opens but this process still reports the
   * pre-install app version until the user restarts into the new APK. WS reconnect would otherwise offer the
   * same update again and re-download. Skip same-version updates until process restart.
   */
  private androidApkInstallLaunchedForVersion: string | null = null
  /** Android: at most one native APK download at a time (duplicate WS/handshake events). */
  private androidUpdateDownloadInFlight: string | null = null

  private async clearAndroidInstallIntentCooldown(): Promise<void> {
    try {
      await getPlatformInstance().storage.remove(ANDROID_INSTALL_INTENT_STORE_KEY)
    }
    catch {
      /* store may be unavailable */
    }
  }

  private async readInstallIntentCooldown(): Promise<{ version: string, at: number } | null> {
    try {
      const raw = await getPlatformInstance().storage.get(ANDROID_INSTALL_INTENT_STORE_KEY)
      if (raw == null || raw === '') {
        return null
      }
      const o = JSON.parse(raw) as { version?: string, at?: number }
      if (typeof o.version !== 'string' || typeof o.at !== 'number') {
        return null
      }
      return { version: o.version.trim(), at: o.at }
    }
    catch {
      return null
    }
  }

  private async writeInstallIntentCooldown(version: string): Promise<void> {
    await getPlatformInstance().storage.set(
      ANDROID_INSTALL_INTENT_STORE_KEY,
      JSON.stringify({ version: version.trim(), at: Date.now() })
    )
  }

  private async readDeferredApkInstall(): Promise<{ version: string, at: number } | null> {
    try {
      const raw = await getPlatformInstance().storage.get(ANDROID_DEFERRED_APK_INSTALL_STORE_KEY)
      if (raw == null || raw === '') {
        return null
      }
      const o = JSON.parse(raw) as { version?: string, at?: number }
      if (typeof o.version !== 'string' || typeof o.at !== 'number') {
        return null
      }
      return { version: o.version.trim(), at: o.at }
    }
    catch {
      return null
    }
  }

  private async writeDeferredApkInstall(version: string): Promise<void> {
    await getPlatformInstance().storage.set(
      ANDROID_DEFERRED_APK_INSTALL_STORE_KEY,
      JSON.stringify({ version: version.trim(), at: Date.now() })
    )
  }

  private async clearDeferredApkInstall(): Promise<void> {
    try {
      await getPlatformInstance().storage.remove(ANDROID_DEFERRED_APK_INSTALL_STORE_KEY)
    }
    catch {
      /* store may be unavailable */
    }
  }

  /** Run PackageInstaller for a fully downloaded `update.apk` (after restart or when policy becomes immediate). */
  private async runAndroidApkInstallFromCachedFile(version: string, otaTraceId?: string): Promise<void> {
    const vNorm = version.trim()
    this.androidApkInstallLaunchedForVersion = vNorm
    await this.writeInstallIntentCooldown(vNorm)
    const { invoke } = await import('@tauri-apps/api/core')
    logPlayerDeviceApkOta({
      phase: 'finish_download_install',
      offeredVersion: vNorm
    }, otaTraceId)
    await invoke('updater_finish_download')
    await this.clearDeferredApkInstall()
  }

  constructor(params: { authenticationService: IAuthenticationService, mode: string, baseURL: string }) {
    super()
    const { authenticationService, mode, baseURL } = params
    this.authenticationService = authenticationService
    this.mode = mode
    this.baseURL = baseURL
    void getPlatformInstance().updater.notifyAppReady()
  }

  async init() {
    await getPlatformInstance().updater.notifyAppReady()
    await this.tryApplyDeferredApkInstallOnStartup()
  }

  /** If a prior session deferred install (`next_app_restart`), open the system installer now. */
  private async tryApplyDeferredApkInstallOnStartup(): Promise<void> {
    if (getPlatform() !== 'android' || await isAndroidPlayStoreDistributionResolved()) {
      return
    }
    const deferred = await this.readDeferredApkInstall()
    if (deferred == null) {
      return
    }
    const vNorm = deferred.version.trim()
    if (this.androidApkInstallLaunchedForVersion === vNorm) {
      return
    }
    if (this.androidUpdateDownloadInFlight != null) {
      return
    }
    try {
      logPlayerDeviceApkOta({
        phase: 'deferred_apply_on_startup',
        offeredVersion: vNorm
      })
      await this.runAndroidApkInstallFromCachedFile(vNorm)
      void this.emit(PlayerUpdateServiceEvent.DOWNLOAD_COMPLETE, { version: vNorm })
    }
    catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      logPlayerDeviceApkOta({
        phase: 'deferred_apply_on_startup_failed',
        offeredVersion: vNorm,
        error: message
      })
      this.androidApkInstallLaunchedForVersion = null
      await this.clearAndroidInstallIntentCooldown()
      if (isDeferredApkCacheInvalidError(message)) {
        await this.clearDeferredApkInstall()
        logPlayerDeviceApkOta({
          phase: 'deferred_apply_cleared_stale_cache',
          offeredVersion: vNorm
        })
      }
    }
  }

  async update(update: IPlayerUpdateEventPayload, opts?: IPlayerUpdaterUpdateOptions) {
    const { version, jwt } = update
    const otaTraceId = update.otaTraceId?.trim() || undefined
    const vNorm = version.trim()

    // Google Play builds must not download or install APKs outside the store update mechanism.
    // Resolve via native shell (survives Web OTA) before Vite env (#1241).
    if (getPlatform() === 'android' && await isAndroidPlayStoreDistributionResolved()) {
      logPlayerDeviceApkOta({
        phase: 'skip_download',
        reason: 'play_store_distribution',
        offeredVersion: vNorm
      }, otaTraceId)
      void this.emit(PlayerUpdateServiceEvent.NO_NEED_UPDATE, { version })
      return
    }

    const timing: TWebOtaApplyTiming = opts?.webOtaApplyTiming ?? 'immediate'
    const deferApkInstallUntilRestart = timing === 'next_app_restart'
    /** Set when Android passes all skip checks and we proceed to download (reused for `start_download` log). */
    let androidOtaDiagForDownload:
      | {
        offeredVersion: string
        embeddedBundleVersion: string
        resolvedPlayerVersion: string
        nativeShellVersion: string | null
        clientVersionUsedForCompare: string
      }
      | undefined
    if (getPlatform() === 'android') {
      if (this.androidApkInstallLaunchedForVersion === vNorm) {
        logPlayerDeviceApkOta({
          phase: 'skip_download',
          reason: 'installer_already_launched_this_process',
          offeredVersion: vNorm
        }, otaTraceId)
        void this.emit(PlayerUpdateServiceEvent.NO_NEED_UPDATE, { version })
        return
      }
      const cooldown = await this.readInstallIntentCooldown()
      if (
        cooldown != null
        && cooldown.version === vNorm
        && Date.now() - cooldown.at < ANDROID_INSTALL_INTENT_COOLDOWN_MS
      ) {
        logPlayerDeviceApkOta({
          phase: 'skip_download',
          reason: 'install_intent_cooldown',
          offeredVersion: vNorm,
          cooldownAgeMs: Date.now() - cooldown.at
        }, otaTraceId)
        void this.emit(PlayerUpdateServiceEvent.NO_NEED_UPDATE, { version })
        return
      }
      const nativeVer = await getNativeShellAppVersionName()
      const resolvedVer = await getResolvedPlayerAppVersion()
      const clientVer = (nativeVer ?? '').trim()
      const versionDiag = {
        offeredVersion: vNorm,
        embeddedBundleVersion: EMBEDDED_PLAYER_PACKAGE_VERSION.trim(),
        resolvedPlayerVersion: resolvedVer,
        nativeShellVersion: nativeVer,
        clientVersionUsedForCompare: clientVer !== '' ? clientVer : '(unknown-shell)'
      }
      const skipForShell = shouldSkipAndroidApkOtaForNativeShell({
        offeredVersion: version,
        nativeShellVersion: nativeVer
      })
      if (skipForShell === 'current') {
        logPlayerDeviceApkOta({
          phase: 'skip_download',
          reason: 'client_at_or_newer_than_offer',
          ...versionDiag
        }, otaTraceId)
        void this.emit(PlayerUpdateServiceEvent.NO_NEED_UPDATE, { version })
        await this.clearAndroidInstallIntentCooldown()
        await this.clearDeferredApkInstall()
        return
      }
      if (skipForShell === 'incomparable') {
        logPlayerDeviceApkOta({
          phase: 'skip_download',
          reason: 'offer_not_newer_than_client_scheme_mismatch_or_unknown',
          ...versionDiag
        }, otaTraceId)
        void this.emit(PlayerUpdateServiceEvent.NO_NEED_UPDATE, { version })
        await this.clearAndroidInstallIntentCooldown()
        return
      }
      if (this.androidUpdateDownloadInFlight != null) {
        if (this.androidUpdateDownloadInFlight === vNorm) {
          logPlayerDeviceApkOta({
            phase: 'skip_download',
            reason: 'download_in_flight_same_version',
            offeredVersion: vNorm
          }, otaTraceId)
          void this.emit(PlayerUpdateServiceEvent.NO_NEED_UPDATE, { version })
        }
        else {
          logPlayerDeviceApkOta({
            phase: 'skip_download',
            reason: 'download_in_flight_other_version',
            offeredVersion: vNorm,
            inFlightVersion: this.androidUpdateDownloadInFlight
          }, otaTraceId)
        }
        return
      }
      if (deferApkInstallUntilRestart) {
        const def = await this.readDeferredApkInstall()
        if (def != null && def.version.trim() === vNorm) {
          logPlayerDeviceApkOta({
            phase: 'skip_download',
            reason: 'deferred_install_pending_restart',
            offeredVersion: vNorm
          }, otaTraceId)
          void this.emit(PlayerUpdateServiceEvent.NO_NEED_UPDATE, { version })
          return
        }
      }
      else {
        const def = await this.readDeferredApkInstall()
        if (def != null && def.version.trim() === vNorm) {
          let finishedDeferredOk = false
          try {
            this.androidUpdateDownloadInFlight = vNorm
            void this.emit(PlayerUpdateServiceEvent.UPDATE_AVAILABLE, { version })
            void this.emit(PlayerUpdateServiceEvent.DOWNLOAD, { version, progress: 100 })
            await this.runAndroidApkInstallFromCachedFile(vNorm, otaTraceId)
            void this.emit(PlayerUpdateServiceEvent.DOWNLOAD_COMPLETE, { version })
            finishedDeferredOk = true
          }
          catch (err) {
            const message = err instanceof Error ? err.message : String(err)
            logPlayerDeviceApkOta({
              phase: 'immediate_finish_deferred_failed',
              error: message,
              offeredVersion: vNorm
            }, otaTraceId)
            this.androidApkInstallLaunchedForVersion = null
            await this.clearAndroidInstallIntentCooldown()
            if (isDeferredApkCacheInvalidError(message)) {
              await this.clearDeferredApkInstall()
            }
            void this.emit(PlayerUpdateServiceEvent.DOWNLOAD_FAILED, { version, error: message })
            void this.emit(PlayerUpdateServiceEvent.UPDATE_FAILED, { version, error: message })
          }
          finally {
            this.androidUpdateDownloadInFlight = null
          }
          if (finishedDeferredOk) {
            return
          }
        }
      }
      androidOtaDiagForDownload = versionDiag
    }
    const platform = getPlatformInstance()
    void this.emit(PlayerUpdateServiceEvent.UPDATE_AVAILABLE, { version })
    if (getPlatform() === 'android') {
      this.androidUpdateDownloadInFlight = vNorm
    }
    const androidInstallPersist = { persisted: false, streamFinishedOk: false }
    try {
      if (getPlatform() === 'android') {
        const d = await this.readDeferredApkInstall()
        if (d != null && d.version.trim() !== vNorm) {
          await this.clearDeferredApkInstall()
        }
      }
      if (androidOtaDiagForDownload != null) {
        logPlayerDeviceApkOta({ phase: 'start_download', ...androidOtaDiagForDownload }, otaTraceId)
      }
      void this.emit(PlayerUpdateServiceEvent.DOWNLOAD, { version, progress: 0 })
      const playerId = await this.authenticationService.getPlayerId()
      const b64PlayerId = btoa(playerId)
      if (getPlatform() === 'android') {
        const runFinishAndInstall = !deferApkInstallUntilRestart
        await this.downloadInVueAndStreamToRust({
          version,
          jwt,
          b64PlayerId,
          runFinishAndInstall,
          onBeforeFinishDownload: async () => {
            this.androidApkInstallLaunchedForVersion = vNorm
            await this.writeInstallIntentCooldown(vNorm)
            androidInstallPersist.persisted = true
          }
        })
        if (deferApkInstallUntilRestart) {
          await this.writeDeferredApkInstall(vNorm)
          logPlayerDeviceApkOta({
            phase: 'apk_streamed_pending_restart',
            offeredVersion: vNorm
          }, otaTraceId)
        }
        androidInstallPersist.streamFinishedOk = true
      }
      else {
        await platform.updater.update({
          version,
          jwt,
          baseURL: this.baseURL,
          b64PlayerId
        })
      }
      const downloadCompletePayload: DownloadCompleteEvent = { version }
      if (getPlatform() === 'android' && deferApkInstallUntilRestart) {
        downloadCompletePayload.installPendingRestart = true
      }
      void this.emit(PlayerUpdateServiceEvent.DOWNLOAD_COMPLETE, downloadCompletePayload)
    }
    catch (err) {
      // Do **not** clear `ANDROID_INSTALL_INTENT_STORE_KEY` when `persisted` is true but
      // `streamFinishedOk` is false: that means the APK finished streaming and we wrote the
      // install-intent record, then `updater_finish_download` failed (e.g. system installer
      // intent). Clearing forced a full re-download on the next WS message or after restart
      // even though `update.apk` was already on disk.
      const message = err instanceof Error ? err.message : String(err)
      if (androidOtaDiagForDownload != null) {
        logPlayerDeviceApkOta({
          phase: 'download_or_install_failed',
          error: message,
          installIntentPersisted: androidInstallPersist.persisted,
          streamFinishedOk: androidInstallPersist.streamFinishedOk,
          ...androidOtaDiagForDownload
        }, otaTraceId)
      }
      if (
        deferApkInstallUntilRestart
        && getPlatform() === 'android'
        && !androidInstallPersist.streamFinishedOk
      ) {
        await this.clearDeferredApkInstall()
      }
      void this.emit(PlayerUpdateServiceEvent.DOWNLOAD_FAILED, { version, error: message })
      void this.emit(PlayerUpdateServiceEvent.UPDATE_FAILED, { version, error: message })
    }
    finally {
      if (getPlatform() === 'android') {
        this.androidUpdateDownloadInFlight = null
      }
    }
  }

  private async downloadInVueAndStreamToRust(params: {
    version: string
    jwt: string
    b64PlayerId: string
    /** When false, stream completes without opening the system installer (deferred until next start). */
    runFinishAndInstall: boolean
    /** Run after the APK is fully streamed, **before** `updater_finish_download` (system installer). Persist cooldown here so a paused/killed WebView still skips re-download on reconnect. */
    onBeforeFinishDownload?: () => Promise<void>
  }): Promise<void> {
    const { version, jwt, b64PlayerId, runFinishAndInstall, onBeforeFinishDownload } = params
    const base = this.baseURL.replace(/\/$/, '')
    const url = `${base}/players/${b64PlayerId}/updates/${jwt}`
    const { invoke } = await import('@tauri-apps/api/core')
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`download failed: ${response.status}`)
    }
    const contentLength = response.headers.get('Content-Length')
    const total = contentLength ? Number.parseInt(contentLength, 10) : 0
    const body = response.body
    if (!body) {
      throw new Error('no response body')
    }
    const reader = body.getReader()
    await invoke('updater_begin_download')
    let received = 0
    let lastEmitPct = -1
    let lastFallbackEmitBytes = 0
    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) { break }
        if (value && value.length > 0) {
          const chunkBase64 = uint8ArrayToBase64(value)
          await invoke('updater_append_chunk', { chunkBase64 })
          received += value.length
          if (total > 0) {
            const pct = Math.floor((received / total) * 100)
            if (pct > lastEmitPct && pct <= 100) {
              lastEmitPct = pct
              void this.emit(PlayerUpdateServiceEvent.DOWNLOAD, { version, progress: pct })
            }
          }
          else if (received - lastFallbackEmitBytes >= 512 * 1024) {
            lastFallbackEmitBytes = received
            const pct = Math.min(95, Math.floor(received / (1024 * 1024)) * 10)
            void this.emit(PlayerUpdateServiceEvent.DOWNLOAD, { version, progress: pct })
          }
        }
      }
      void this.emit(PlayerUpdateServiceEvent.DOWNLOAD, { version, progress: 100 })
      if (runFinishAndInstall) {
        if (onBeforeFinishDownload != null) {
          await onBeforeFinishDownload()
        }
        await invoke('updater_finish_download')
      }
    }
    finally {
      reader.releaseLock()
    }
  }

  async reset() {
    console.warn('Got reset message...')
    this.androidApkInstallLaunchedForVersion = null
    this.androidUpdateDownloadInFlight = null
    await this.clearAndroidInstallIntentCooldown()
    await this.clearDeferredApkInstall()
    await getPlatformInstance().updater.reset()
  }
}
